import { CHANNELS } from "../../shared/channels";
import { DEFAULT_PATTERN_ID, DEFAULT_THEME_ID, PATTERNS, THEME_GROUPS, THEMES, patternById, themeById } from "../../shared/themes";
import { PlaneBoard, SecretName, SecretStatus, UserSettings, WebTab } from "../../shared/types";
import { call } from "../bridge";
import { byId, cloneTemplate, fillFields } from "../dom";
import { onViewShown } from "../navigation";
import { amOwner, canUse } from "../me";
import { paintAmbientStill } from "../ambient";
import { activeThemeId, paintPattern, paintTheme, rememberChosenTheme } from "../theme";
import { applyLook, currentLook } from "../appearance";
import { cleanLook } from "../../shared/look_prefs";
import { setupLookStudio, toast } from "./look_studio";
import { flash } from "../tweens";
import { renderTabButtons } from "./web_view";
import { rememberSettings } from "../settings_cache";
import { setupSettingsActions } from "./settings_actions";
import { CURRENCIES } from "../../shared/money";
import { LANGUAGES, languageById } from "../../shared/i18n";
import { setLanguage, t } from "../i18n";

// settings form, text fields map straight onto UserSettings by their name attribute

let settings: UserSettings;
let boardsLoaded = false;

const studio = setupLookStudio({
  changed(next) {
    settings = next;
    paintThemes();
    paintPatterns();
  }
});

const form = () => byId<HTMLFormElement>("settings-form");

function tabsToText(tabs: WebTab[]): string {
  return tabs.map((tab) => `${tab.name} | ${tab.url}`).join("\n");
}

function textToTabs(text: string): WebTab[] {
  return text.split("\n").map((line) => line.split("|").map((part) => part.trim()))
    .filter(([name, url]) => name && /^https?:\/\//.test(url ?? ""))
    .map(([name, url]) => ({ id: name.toLowerCase().replace(/[^a-z0-9]+/g, "-"), name, url }));
}

function paintThemes(): void {
  const current = themeById(settings.themeId || DEFAULT_THEME_ID).id;
  const board = byId("theme-board");
  // adult themes only show while this person has turned them on in the look studio
  const mature = cleanLook(settings.look).mature;
  board.replaceChildren(...THEME_GROUPS.filter((group) => THEMES.some((item) => item.group === group.id && (mature || !item.mature))).map((group) => {
    const wrap = document.createElement("section");
    const title = document.createElement("h2");
    title.className = "marker theme-group-title";
    title.textContent = t(group.label);
    const grid = document.createElement("div");
    grid.className = "theme-grid";
    for (const theme of THEMES.filter((item) => item.group === group.id && (mature || !item.mature))) {
      const card = document.createElement("button");
      card.type = "button";
      card.className = "theme-card";
      card.classList.toggle("active", theme.id === current);
      card.setAttribute("aria-pressed", String(theme.id === current));
      const swatch = document.createElement("span");
      swatch.className = "theme-swatch";
      swatch.style.background = `linear-gradient(90deg, ${theme.preview.case} 0 46%, ${theme.preview.tube} 46% 78%, ${theme.preview.ink} 78% 100%)`;
      const name = document.createElement("span");
      name.className = "theme-name";
      name.textContent = theme.label;
      const blurb = document.createElement("span");
      blurb.className = "theme-blurb";
      blurb.textContent = t(theme.blurb);
      card.append(swatch, name, blurb);
      card.addEventListener("click", () => void pickTheme(theme.id));
      grid.append(card);
    }
    wrap.append(title, grid);
    return wrap;
  }));
}

function paintPatterns(): void {
  const current = patternById(settings.patternId || DEFAULT_PATTERN_ID).id;
  byId("pattern-board").replaceChildren(...PATTERNS.map((pattern) => {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "pattern-chip";
    chip.classList.toggle("active", pattern.id === current);
    chip.setAttribute("aria-pressed", String(pattern.id === current));
    const swatch = document.createElement(pattern.animated ? "canvas" : "span");
    swatch.className = "pattern-swatch";
    swatch.dataset.pattern = pattern.id;
    // the pane can be hidden when this runs, so draw the still once the swatch has a size
    if (pattern.animated) new ResizeObserver(() => paintAmbientStill(swatch as HTMLCanvasElement, pattern.id)).observe(swatch);
    const name = document.createElement("span");
    name.className = "theme-name";
    name.textContent = t(pattern.label);
    chip.append(swatch, name);
    if (pattern.animated) chip.title = t("moves gently, stays still with calm motion");
    chip.addEventListener("click", () => void pickPattern(pattern.id));
    return chip;
  }));
}

async function pickPattern(patternId: string): Promise<void> {
  paintPattern(patternId);
  settings = await call<UserSettings>(CHANNELS.settingsSave, { patternId });
  rememberSettings(settings);
  paintPatterns();
  studio.show(settings);
  toast("pattern on");
}

async function pickTheme(themeId: string): Promise<void> {
  rememberChosenTheme(themeId);
  const changes: Partial<UserSettings> = { themeId };
  // following the device, the pick fills the slot for the mode this device is in right now
  if (currentLook().autoOn) {
    const dark = matchMedia("(prefers-color-scheme: dark)").matches;
    changes.look = cleanLook({ ...currentLook(), [dark ? "autoDark" : "autoLight"]: themeId });
    applyLook(changes.look);
  }
  paintTheme(activeThemeId());
  settings = await call<UserSettings>(CHANNELS.settingsSave, changes);
  studio.show(settings);
  rememberSettings(settings);
  paintThemes();
  toast("theme on");
}

async function fillForm(): Promise<void> {
  settings = await call<UserSettings>(CHANNELS.settingsGet);
  // keys are fractal's, nobody else gets to see which are set
  const secrets = amOwner() ? await call<SecretStatus>(CHANNELS.secretsStatus) : {} as SecretStatus;

  byId<HTMLSelectElement>("settings-currency").value = settings.currency || "auto";
  byId<HTMLSelectElement>("settings-language").value = languageById(settings.language).id;
  form().querySelectorAll<HTMLInputElement>("input[name]").forEach((input) => {
    const value = settings[input.name as keyof UserSettings];
    if (input.type === "checkbox") input.checked = Boolean(value);
    else input.value = String(value ?? "");
  });

  form().querySelectorAll<HTMLInputElement>("input[data-secret]").forEach((input) => {
    input.value = "";
    input.placeholder = secrets[input.dataset.secret as SecretName] ? t("saved, type to replace") : t("not set");
  });

  byId<HTMLTextAreaElement>("settings-tabs").value = tabsToText(settings.webTabs);
  byId<HTMLInputElement>("settings-token").value = settings.extensionToken;
  studio.show(settings);
  rememberChosenTheme(settings.themeId || DEFAULT_THEME_ID);
  paintTheme(activeThemeId());
  paintPattern(settings.patternId || DEFAULT_PATTERN_ID);
  paintThemes();
  paintPatterns();
}

// everyone's own token for the browser extension, only on the web since that's where it saves to
async function fillExtensionToken(): Promise<void> {
  const allowed = canUse("refs", "edit");
  byId("settings-ext-panel").classList.toggle("feature-off", !allowed);
  if (!document.body.classList.contains("is-web") || !allowed) return;
  byId<HTMLInputElement>("settings-ext-token").value = await call<string>(CHANNELS.extensionTokenGet).catch(() => "");
}

async function loadBoards(): Promise<void> {
  try {
    await saveForm();
    const boards = await call<PlaneBoard[]>(CHANNELS.tasksBoards);
    const rows = boards.map((board) => {
      const row = fillFields(cloneTemplate("template-board"), { name: `${board.name} (${board.identifier})` });
      const checkbox = row.querySelector("input")!;
      checkbox.value = board.id;
      checkbox.checked = settings.planeBoardIds.includes(board.id);
      return row;
    });
    byId("settings-boards").replaceChildren(...rows);
    boardsLoaded = true;
  } catch (error) {
    byId("settings-status").textContent = String(error);
  }
}

async function saveForm(): Promise<void> {
  const changes: Partial<UserSettings> = {};
  form().querySelectorAll<HTMLInputElement>("input[name]").forEach((input) => {
    (changes as Record<string, unknown>)[input.name] = input.type === "checkbox" ? input.checked : input.value.trim();
  });
  changes.webTabs = textToTabs(byId<HTMLTextAreaElement>("settings-tabs").value);

  if (boardsLoaded) {
    const checked = byId("settings-boards").querySelectorAll<HTMLInputElement>("input:checked");
    changes.planeBoardIds = [...checked].map((checkbox) => checkbox.value);
  }

  for (const input of form().querySelectorAll<HTMLInputElement>("input[data-secret]")) {
    if (input.value.trim()) await call(CHANNELS.secretsSet, input.dataset.secret, input.value);
  }

  settings = await call<UserSettings>(CHANNELS.settingsSave, changes);
  rememberSettings(settings);
  rememberChosenTheme(settings.themeId || DEFAULT_THEME_ID);
  paintTheme(activeThemeId());
  renderTabButtons(settings);
  await fillForm();
  if (canUse("tasks")) await call(CHANNELS.tasksList, true).catch(() => undefined);
}

export async function setupSettingsView(): Promise<void> {
  form().addEventListener("submit", async (event) => {
    event.preventDefault();
    await saveForm();
    byId("settings-status").textContent = t("saved");
    flash(byId("settings-status"));
  });
  byId("settings-load-boards").addEventListener("click", loadBoards);
  byId("settings-ext-copy").addEventListener("click", async () => {
    await navigator.clipboard.writeText(byId<HTMLInputElement>("settings-ext-token").value).catch(() => undefined);
    byId("settings-status").textContent = t("token copied");
    flash(byId("settings-status"));
  });
  byId("settings-ext-reset").addEventListener("click", async () => {
    if (!confirm(t("make a new extension token? the old one stops working until you paste the new one in"))) return;
    byId<HTMLInputElement>("settings-ext-token").value = await call<string>(CHANNELS.extensionTokenReset);
    byId("settings-status").textContent = t("new token made");
    flash(byId("settings-status"));
  });
  byId("settings-logout-all").addEventListener("click", async () => {
    if (!confirm(t("log out of chix on every device?"))) return;
    await fetch("/auth/logout-all", { method: "POST", credentials: "include" }).catch(() => undefined);
    location.reload();
  });
  const currency = byId<HTMLSelectElement>("settings-currency");
  currency.replaceChildren(...CURRENCIES.map((item) => Object.assign(document.createElement("option"), { value: item.id, textContent: t(item.label) })));
  currency.addEventListener("change", async () => {
    settings = await call<UserSettings>(CHANNELS.settingsSave, { currency: currency.value });
    rememberSettings(settings);
    window.dispatchEvent(new CustomEvent("chi-currency", { detail: currency.value }));
    flash(currency);
  });
  const language = byId<HTMLSelectElement>("settings-language");
  language.replaceChildren(...LANGUAGES.map((item) => Object.assign(document.createElement("option"), { value: item.id, textContent: item.label })));
  language.addEventListener("change", async () => {
    settings = await call<UserSettings>(CHANNELS.settingsSave, { language: language.value });
    rememberSettings(settings);
    // every view wrote its words at boot, so a fresh start is the one way they all switch together
    if (setLanguage(settings.language)) location.reload();
  });
  setupSettingsActions(async () => {
    await fillForm();
    const secrets = amOwner() ? await call<SecretStatus>(CHANNELS.secretsStatus) : {} as SecretStatus;
    if (secrets.planeApiKey && settings.planeWorkspace) await loadBoards();
  });
  onViewShown("settings", (detail) => {
    const pane = detail || "sys";
    document.querySelectorAll<HTMLElement>(".settings-pane").forEach((el) => {
      el.hidden = el.id !== `settings-pane-${pane}`;
    });
    void fillForm();
    void fillExtensionToken();
  });
}
